import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ClipboardCheck, ClipboardX, Bell, Search, Calendar, Building2, MapPin, User, RefreshCw,
  CheckCircle2, AlertCircle, FileX, Loader2, Eye, X, Send, Trash2, Truck,
} from 'lucide-react';
import FormCompletionChart from '../components/planning/FormCompletionChart';

/**
 * Planlama Formları Takip (Faz 4 — Şub 03, 2026)
 * --------------------------------------------------------------
 * Admin sayfası: tarih aralığında tüm atamaların form durumunu listeler.
 * Filtreler: tarih, firma, depo, durum (Doldurdu/Bekliyor).
 * Aksiyonlar: tekil "Hatırlat" + "Tümüne hatırlat" (cron çalıştır)
 */
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function PlanningFormSubmissions() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, submitted: 0, pending: 0, no_template: 0 });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(todayIso());
  const [status, setStatus] = useState('pending'); // all | submitted | pending
  const [search, setSearch] = useState('');
  const [reminding, setReminding] = useState(null);
  const [bulkReminding, setBulkReminding] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  // ─── Haz 14, 2026 — Toplu silme + sayfalama ─────────────────────────────
  // Bulk select: assignment_id'leri tutulan Set state'i.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Pagination — kayıt sayısı 100+'larda sayfa boyunca takılmasın diye.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const v = parseInt(localStorage.getItem('planning_forms_page_size') || '50', 10);
    return [25, 50, 100, 200].includes(v) ? v : 50;
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (status === 'submitted') params.set('submitted', 'true');
      if (status === 'pending') params.set('submitted', 'false');
      const res = await api.get(`/planning/form-submissions?${params.toString()}`);
      let data = res.data?.data || [];
      // status=pending UI tarafında has_template filtresi de uygula
      if (status === 'pending') data = data.filter((d) => d.has_template);
      setItems(data);
      setSummary(res.data?.summary || {});
    } catch (err) {
      toast.error('Form listesi yüklenemedi');
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, status]);

  useEffect(() => { reload(); }, [reload]);

  // Arama filtresi (client-side)
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) =>
      `${i.employee_name || ''} ${i.route_code || ''} ${i.depot_name || ''} ${i.company_name || ''}`
        .toLowerCase().includes(s)
    );
  }, [items, search]);

  const handleRemindOne = async (assignmentId) => {
    setReminding(assignmentId);
    try {
      await api.post(`/planning/form-submissions/${assignmentId}/remind`);
      toast.success('Hatırlatma gönderildi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gönderilemedi');
    } finally { setReminding(null); }
  };

  const handleRemindAll = async () => {
    if (!window.confirm('Bugünkü tüm doldurmamış çalışanlara hatırlatma gönderilecek. Devam edilsin mi?')) return;
    setBulkReminding(true);
    try {
      const res = await api.post('/planning/form-reminders/run-now');
      toast.success(`${res.data?.data?.sent || 0} kişiye hatırlatma gönderildi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toplu hatırlatma başarısız');
    } finally { setBulkReminding(false); }
  };

  // Şub 09, 2026 — Form submission'ı + ekleri sil. Atama silinmez, sürücü tekrar doldurabilir.
  const [deleting, setDeleting] = useState(null);
  // Şub 16, 2026 — Formu Routela (db.routes) aktarma; iki aşamalı:
  //   1) Tıklama → `dry_run=true` ile preview modal aç.
  //   2) Modal'da "Onayla" → real convert. "İptal" → modal kapanır.
  // İdempotent: backend daha önce aktarılmış formu mevcut route ile döner.
  // Şub 16, 2026 — Inline edit: modal içinde plaka / hourly_rate / start_cost
  // düzeltilebilir; toplam maliyet client'ta live recalculate olur ve onay
  // anında `overrides` payload backend'e gönderilir.
  const [converting, setConverting] = useState(null);
  const [convertPreview, setConvertPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEdits, setPreviewEdits] = useState({ license_plate: '', hourly_rate: 40, start_cost: 0 });

  const handleConvertToRoute = async (assignmentId) => {
    setPreviewLoading(true);
    setConverting(assignmentId);
    try {
      const res = await api.post(`/planning/form-submissions/${assignmentId}/convert-to-route?dry_run=true`);
      const d = res.data?.data || {};
      const rp = d.route_preview || {};
      setConvertPreview({
        assignment_id: assignmentId,
        route_preview: rp,
        source: d.source || {},
        already_converted: !!d.already_converted,
        plate_missing: !!d.plate_missing,
      });
      // Inline edit alanlarını preview'deki değerlerle doldur
      setPreviewEdits({
        license_plate: rp.license_plate || '',
        hourly_rate: Number(rp.hourly_rate ?? 40),
        start_cost: Number(rp.start_cost ?? 0),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Önizleme alınamadı');
    } finally {
      setPreviewLoading(false);
      setConverting(null);
    }
  };

  // Edit alanlarına göre client-side live recalc (görsel feedback).
  // Backend zaten authoritative olarak yeniden hesaplar — burada sadece
  // operatör değişikliği görsün diye gösteriyoruz.
  const editedTotals = useMemo(() => {
    if (!convertPreview) return null;
    const rp = convertPreview.route_preview || {};
    const calcType = convertPreview.source?.calc_type || 'hours_rate';
    const rate = Number(previewEdits.hourly_rate) || 0;
    const sc = Number(previewEdits.start_cost) || 0;
    const wh = Number(rp.work_hours) || 0;
    let workCost; let totalCost;
    if (calcType === 'manual_total') {
      workCost = 0; totalCost = sc;
    } else if (calcType === 'stops_price') {
      workCost = 0; totalCost = Number(rp.stops || 0) * sc;
    } else {
      workCost = wh * rate; totalCost = workCost + sc;
    }
    return { workCost: Math.round(workCost * 100) / 100, totalCost: Math.round(totalCost * 100) / 100 };
  }, [convertPreview, previewEdits]);

  const handleConfirmConvert = async () => {
    if (!convertPreview) return;
    const assignmentId = convertPreview.assignment_id;
    setConverting(assignmentId);
    try {
      // Override'ları sadece değer dolu/geçerli ise gönder
      const overrides = {};
      const trimPlate = String(previewEdits.license_plate || '').trim();
      if (trimPlate) overrides.license_plate = trimPlate;
      const hr = Number(previewEdits.hourly_rate);
      if (Number.isFinite(hr) && hr > 0) overrides.hourly_rate = hr;
      const sc = Number(previewEdits.start_cost);
      if (Number.isFinite(sc)) overrides.start_cost = sc;

      const res = await api.post(
        `/planning/form-submissions/${assignmentId}/convert-to-route`,
        overrides,
      );
      const d = res.data?.data || {};
      const r = d.route || {};
      if (d.already_converted) {
        toast.info(`Bu form zaten aktarılmıştı (${r.customer || ''} · ${r.date || ''})`);
      } else {
        toast.success(`Routela aktarıldı: ${r.customer || ''} · ${r.license_plate || ''} · ${r.km_total || 0} km`);
      }
      setConvertPreview(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Routela aktarım başarısız');
    } finally { setConverting(null); }
  };

  const handleDeleteSubmission = async (assignmentId) => {
    if (!window.confirm('Bu form yanıtı ve ekleri silinecek. Sürücü tekrar form doldurabilir. Devam edilsin mi?')) return;
    setDeleting(assignmentId);
    try {
      const res = await api.delete(`/planning/form-submissions/${assignmentId}`);
      const d = res.data?.data || {};
      toast.success(`Form silindi (${d.submissions_deleted || 0} yanıt, ${d.attachments_deleted || 0} ek)`);
      await reload();
      setViewItem(null);
      setSelectedIds((prev) => {
        const next = new Set(prev); next.delete(assignmentId); return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Silme başarısız');
    } finally { setDeleting(null); }
  };

  // ─── Bulk delete (Haz 14, 2026) ───────────────────────────────────────
  // İki davranış (satır durumuna göre):
  //  · Dolu form (is_submitted veya eki var) → `DELETE /form-submissions/{id}`
  //    → SADECE form yanıtı + ekleri silinir, atama korunur (sürücü tekrar
  //    doldurabilir).
  //  · Bekleyen / boş → `DELETE /assignments/{id}` → ATAMA tamamen silinir.
  // Tek loop, satır başına uygun endpoint seçilir. Onay mesajında karışım
  // durumunu özetler.
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Her id için karar ver
    const itemMap = new Map(items.map((it) => [it.assignment_id, it]));
    const formIds = [];
    const assignmentIds = [];
    for (const id of ids) {
      const it = itemMap.get(id);
      const hasData = it && (it.is_submitted || (Array.isArray(it.attachments) && it.attachments.length > 0));
      if (hasData) formIds.push(id);
      else assignmentIds.push(id);
    }
    const parts = [];
    if (formIds.length) parts.push(`${formIds.length} dolu form yanıtı (atama kalır)`);
    if (assignmentIds.length) parts.push(`${assignmentIds.length} bekleyen ATAMA (tamamen silinir)`);
    if (!window.confirm(`Silinecek:\n• ${parts.join('\n• ')}\n\nDevam edilsin mi?`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const id of formIds) {
      try { await api.delete(`/planning/form-submissions/${id}`); ok += 1; }
      catch { fail += 1; }
    }
    for (const id of assignmentIds) {
      try { await api.delete(`/planning/assignments/${id}`); ok += 1; }
      catch { fail += 1; }
    }
    if (ok > 0) toast.success(`${ok} kayıt silindi${fail > 0 ? `, ${fail} hatalı` : ''}`);
    else toast.error('Hiçbir kayıt silinemedi');
    setSelectedIds(new Set());
    setBulkDeleting(false);
    await reload();
  };

  // Pagination — filtre/arama değişince sayfa 1'e döner.
  useEffect(() => { setPage(1); }, [search, status, dateFrom, dateTo, items.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // Tüm satırlar seçilebilir (Haz 14 V3): dolu form → form yanıtını sil;
  // bekleyen → atamayı komple sil (handleBulkDelete satır başına karar verir).
  const selectableOnPage = paged;
  const allOnPageSelected = selectableOnPage.length > 0 &&
    selectableOnPage.every((it) => selectedIds.has(it.assignment_id));
  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) selectableOnPage.forEach((it) => next.delete(it.assignment_id));
      else selectableOnPage.forEach((it) => next.add(it.assignment_id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="planning-form-submissions-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-500" /> Planlama Formları
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Atamaya bağlı günlük form doldurma takibi. Kim doldurdu / kim doldurmadı.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {selectedIds.size} kayıt sil
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleRemindAll}
            disabled={bulkReminding}
            data-testid="bulk-remind-btn"
          >
            {bulkReminding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />}
            Bugün için hatırlat
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-slate-500 font-semibold">Toplam Atama</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100" data-testid="summary-total">{summary.total}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Doldurdu</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="summary-submitted">{summary.submitted}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-rose-600 font-semibold flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Bekliyor</p>
          <p className="text-2xl font-bold text-rose-600" data-testid="summary-pending">{summary.pending}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-slate-400 font-semibold flex items-center gap-1"><FileX className="h-3 w-3" /> Şablonsuz</p>
          <p className="text-2xl font-bold text-slate-400">{summary.no_template}</p>
        </CardContent></Card>
      </div>

      {/* Completion trend mini-chart */}
      <FormCompletionChart dateFrom={dateFrom} dateTo={dateTo} />

      {/* Filters */}
      <Card>
        <CardContent className="pt-5 pb-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Başlangıç</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm w-36" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Bitiş</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm w-36" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Durum</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="block h-9 text-sm rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 px-2 min-w-[140px]"
              data-testid="status-filter"
            >
              <option value="all">Hepsi</option>
              <option value="submitted">Dolduran</option>
              <option value="pending">Bekleyen</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Ara</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Çalışan, rota, depo, firma…" className="h-9 text-sm pl-7" />
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={reload} className="gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Yenile
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">
            {filtered.length} kayıt
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3 overflow-x-auto">
          {loading ? (
            <div className="text-center text-slate-400 py-8 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Filtreyle eşleşen kayıt yok.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="text-center py-2 px-2 w-8">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={togglePageSelection}
                      disabled={selectableOnPage.length === 0}
                      title={selectableOnPage.length === 0 ? 'Bu sayfada silinebilir kayıt yok' : (allOnPageSelected ? 'Tümünü kaldır' : 'Tümünü seç')}
                      data-testid="bulk-select-page"
                    />
                  </th>
                  <th className="text-left py-2 px-2 w-24"><Calendar className="inline h-3 w-3 mr-1" />Tarih</th>
                  <th className="text-left py-2 px-2"><User className="inline h-3 w-3 mr-1" />Çalışan</th>
                  <th className="text-left py-2 px-2 w-32">Rota</th>
                  <th className="text-left py-2 px-2 w-28"><MapPin className="inline h-3 w-3 mr-1" />Depo</th>
                  <th className="text-left py-2 px-2 w-28"><Building2 className="inline h-3 w-3 mr-1" />Firma</th>
                  <th className="text-center py-2 px-2 w-32">Durum</th>
                  <th className="text-right py-2 px-2 w-44">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((it) => {
                  const hasData = it.is_submitted || (Array.isArray(it.attachments) && it.attachments.length > 0);
                  const isSelected = selectedIds.has(it.assignment_id);
                  return (
                  <tr key={it.assignment_id} className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${isSelected ? 'bg-rose-50/40 dark:bg-rose-950/20' : ''}`} data-testid={`form-row-${it.assignment_id}`}>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(it.assignment_id)}
                        title={hasData ? 'Seç → form yanıtı silinir (atama kalır, sürücü tekrar doldurabilir)' : 'Seç → ATAMA tamamen silinir'}
                        data-testid={`form-row-checkbox-${it.assignment_id}`}
                      />
                    </td>
                    <td className="py-2 px-2 text-xs">{fmtDate(it.date)}</td>
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-800 dark:text-slate-100">{it.employee_name || '—'}</div>
                      {it.employee_email && <div className="text-[10px] text-slate-400">{it.employee_email}</div>}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">{it.route_code || '—'}</td>
                    <td className="py-2 px-2 text-xs">{it.depot_name || '—'}</td>
                    <td className="py-2 px-2 text-xs">{it.company_name || '—'}</td>
                    <td className="py-2 px-2 text-center">
                      {!it.has_template ? (
                        <Badge variant="outline" className="text-[10px] gap-0.5"><FileX className="h-2.5 w-2.5" /> Şablon yok</Badge>
                      ) : it.is_submitted ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5">
                          <CheckCircle2 className="h-2.5 w-2.5" /> Dolduruldu
                        </Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 gap-0.5">
                          <AlertCircle className="h-2.5 w-2.5" /> Bekliyor
                        </Badge>
                      )}
                      {it.is_submitted && (
                        <div className="text-[9px] text-slate-400 mt-0.5">{fmtDateTime(it.submitted_at)}</div>
                      )}
                      {/* Şub 16, 2026 — Kaynak badge: çalışan kendi bildirdi mi? */}
                      {it.source === 'self-report' && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-0.5 text-[9px] mt-1 block w-fit">
                          👤 Kendi bildirdi
                        </Badge>
                      )}
                      {Array.isArray(it.attachments) && it.attachments.length > 0 && (
                        <Badge variant="outline" className="text-[9px] mt-1 gap-0.5 text-blue-600 border-blue-200">
                          📎 {it.attachments.length} ek
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="inline-flex gap-1">
                        {(it.is_submitted || (Array.isArray(it.attachments) && it.attachments.length > 0)) ? (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewItem(it)} title="Yanıtları ve ekleri gör" data-testid={`form-view-${it.assignment_id}`}>
                              <Eye className="h-3.5 w-3.5 text-blue-500" />
                            </Button>
                            {/* Şub 16, 2026 — Formu Routela (Turlar) aktar (preview modal'lı) */}
                            {it.is_submitted && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className={`h-7 w-7 ${it.converted_route_id ? 'opacity-60' : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20'}`}
                                onClick={() => handleConvertToRoute(it.assignment_id)}
                                disabled={(converting === it.assignment_id) || (previewLoading && converting === it.assignment_id)}
                                title={it.converted_route_id ? `Aktarıldı (${it.converted_at?.slice(0, 10) || ''})` : 'Routelara aktar (önizleme aç)'}
                                data-testid={`form-convert-route-${it.assignment_id}`}
                              >
                                {(converting === it.assignment_id || (previewLoading && converting === it.assignment_id))
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />
                                  : it.converted_route_id
                                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                    : <Truck className="h-3.5 w-3.5 text-emerald-600" />}
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                              onClick={() => handleDeleteSubmission(it.assignment_id)}
                              disabled={deleting === it.assignment_id}
                              title="Formu ve eklerini sil (sürücü tekrar doldurabilir)"
                              data-testid={`form-delete-${it.assignment_id}`}
                            >
                              {deleting === it.assignment_id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin text-rose-500" />
                                : <Trash2 className="h-3.5 w-3.5 text-rose-500" />}
                            </Button>
                          </>
                        ) : it.has_template ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                              onClick={() => handleRemindOne(it.assignment_id)}
                              disabled={reminding === it.assignment_id}
                              data-testid={`remind-${it.assignment_id}`}
                            >
                              {reminding === it.assignment_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                              Hatırlat
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                              onClick={async () => {
                                if (!window.confirm(`${it.employee_name} · ${it.route_code} · ${fmtDate(it.date)}\n\nBu ATAMA tamamen silinecek (form henüz doldurulmamış). Devam?`)) return;
                                setDeleting(it.assignment_id);
                                try {
                                  await api.delete(`/planning/assignments/${it.assignment_id}`);
                                  toast.success('Atama silindi');
                                  await reload();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Atama silinemedi');
                                } finally { setDeleting(null); }
                              }}
                              disabled={deleting === it.assignment_id}
                              title="Atamayı sil (form yok)"
                              data-testid={`assignment-delete-${it.assignment_id}`}
                            >
                              {deleting === it.assignment_id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin text-rose-500" />
                                : <Trash2 className="h-3.5 w-3.5 text-rose-500" />}
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                            onClick={async () => {
                              if (!window.confirm(`${it.employee_name} · ${it.route_code} · ${fmtDate(it.date)}\n\nBu ATAMA tamamen silinecek. Devam?`)) return;
                              setDeleting(it.assignment_id);
                              try {
                                await api.delete(`/planning/assignments/${it.assignment_id}`);
                                toast.success('Atama silindi');
                                await reload();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Atama silinemedi');
                              } finally { setDeleting(null); }
                            }}
                            disabled={deleting === it.assignment_id}
                            title="Atamayı sil"
                            data-testid={`assignment-delete-${it.assignment_id}`}
                          >
                            {deleting === it.assignment_id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin text-rose-500" />
                              : <Trash2 className="h-3.5 w-3.5 text-rose-500" />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {/* Pagination footer */}
          {!loading && filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-2 py-3 mt-2 border-t border-slate-100 dark:border-slate-800 text-xs" data-testid="planning-forms-pagination">
              <div className="text-slate-500 dark:text-slate-400">
                {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} / <span className="font-semibold text-slate-700 dark:text-slate-200">{filtered.length}</span> kayıt
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 dark:text-slate-400">Sayfa başına:</span>
                <select
                  className="h-7 px-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs"
                  value={pageSize}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setPageSize(v);
                    localStorage.setItem('planning_forms_page_size', String(v));
                    setPage(1);
                  }}
                  data-testid="planning-forms-pagesize"
                >
                  {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="flex items-center gap-1 ml-2">
                  <Button size="icon" variant="outline" className="h-7 w-7" disabled={currentPage <= 1} onClick={() => setPage(1)} title="İlk sayfa">«</Button>
                  <Button size="icon" variant="outline" className="h-7 w-7" disabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} title="Önceki">‹</Button>
                  <span className="mx-1 text-slate-600 dark:text-slate-300 tabular-nums"><span className="font-semibold">{currentPage}</span> / {totalPages}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" disabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} title="Sonraki">›</Button>
                  <Button size="icon" variant="outline" className="h-7 w-7" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)} title="Son sayfa">»</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submission detail dialog */}
      {viewItem && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setViewItem(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h2 className="text-base font-bold">{viewItem.route_code} formu</h2>
                <p className="text-xs text-slate-500">{viewItem.employee_name} · {fmtDate(viewItem.date)}</p>
              </div>
              <button onClick={() => setViewItem(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4">
              {!viewItem.answers || Object.keys(viewItem.answers).length === 0 ? (
                <p className="text-sm text-slate-400 italic">Yanıt bulunamadı.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(viewItem.answers).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3 text-xs text-slate-500 capitalize">{k.replace(/_/g, ' ')}</td>
                        <td className="py-1.5 font-medium text-slate-800 dark:text-slate-100 break-all">
                          {typeof v === 'boolean' ? (v ? 'Evet' : 'Hayır') : String(v ?? '—')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Attachments preview */}
              {Array.isArray(viewItem.attachments) && viewItem.attachments.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1">
                    📎 Ek Dosyalar ({viewItem.attachments.length})
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid="submission-attachments">
                    {viewItem.attachments.map((a) => {
                      const isImg = (a.content_type || '').startsWith('image/');
                      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/planning/form-attachments/${a.id}/download?preview=1`;
                      return (
                        <a
                          key={a.id}
                          href={a.cdn_url || downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block rounded border border-slate-200 dark:border-slate-700 overflow-hidden hover:border-blue-400 transition-colors"
                          title={a.file_name}
                          data-testid={`attach-thumb-${a.id}`}
                        >
                          {isImg && a.cdn_url ? (
                            <img src={a.cdn_url} alt={a.file_name} className="w-full aspect-square object-cover" />
                          ) : (
                            <div className="w-full aspect-square flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-400">
                              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="text-[10px] p-1 truncate bg-slate-50 dark:bg-slate-900 group-hover:text-blue-600">
                            {a.file_name}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-[10px] text-slate-400 mt-3">Gönderim: {fmtDateTime(viewItem.submitted_at) || '—'}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Routela Aktar Önizleme Modal'ı (Şub 16, 2026) ─────────────────────
          Backend dry_run sonucunu gösterir; admin değerleri kontrol edip
          "Onayla ve Aktar" der. Idempotency: zaten aktarılmışsa kırmızı uyarı
          gösterilir, yine de "Aynısını gör" → mevcut route döner. */}
      {convertPreview && (
        <div className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConvertPreview(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="convert-preview-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <div className="font-semibold flex items-center gap-2">
                <Truck className="h-4 w-4 text-emerald-600" />
                Routela Aktarım Önizlemesi
              </div>
              <button onClick={() => setConvertPreview(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 py-3 space-y-3">
              {convertPreview.already_converted && (
                <div className="flex items-start gap-2 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 text-xs text-amber-800 dark:text-amber-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Bu form daha önce Routela aktarılmış. Onaylarsanız mevcut Route döner — duplicate yazılmaz.
                  </div>
                </div>
              )}

              {convertPreview.plate_missing && !previewEdits.license_plate?.trim() && (
                <div className="flex items-start gap-2 px-3 py-2 rounded bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700/40 text-xs text-rose-800 dark:text-rose-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Bu formda plaka alanı doldurulmamış. Aşağıdaki "Plaka" kutusuna plakayı yazıp onaylayın.
                  </div>
                </div>
              )}

              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Kaynak Form</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs bg-slate-50 dark:bg-slate-800/40 rounded px-3 py-2">
                <PreviewRow label="Firma" value={convertPreview.source?.company_name || convertPreview.source?.company_slug || '—'} />
                <PreviewRow label="Müşteri slug" value={convertPreview.source?.company_slug || '—'} mono />
                <PreviewRow label="Rota No" value={convertPreview.source?.route_code || '—'} mono />
                <PreviewRow label="Hesap Tipi" value={convertPreview.source?.calc_type || '—'} mono />
              </div>

              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-2 flex items-center gap-2">
                Oluşturulacak Route
                <span className="text-[9px] font-normal italic text-slate-400 normal-case">— sarı alanlar düzenlenebilir</span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs bg-emerald-50/40 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded px-3 py-2">
                <PreviewRow label="Tarih" value={convertPreview.route_preview?.date || '—'} mono />
                <PreviewRow label="Müşteri" value={convertPreview.route_preview?.customer || '—'} mono />

                {/* Editable: Plaka */}
                <div className="text-slate-500 dark:text-slate-400 self-center flex items-center gap-1">
                  Plaka
                  {convertPreview.plate_missing && !previewEdits.license_plate?.trim() && (
                    <span className="text-[9px] font-semibold text-rose-600 dark:text-rose-400">* zorunlu</span>
                  )}
                </div>
                <input
                  type="text"
                  value={previewEdits.license_plate}
                  onChange={(e) => setPreviewEdits((s) => ({ ...s, license_plate: e.target.value }))}
                  className={`font-mono font-semibold border rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 ${
                    convertPreview.plate_missing && !previewEdits.license_plate?.trim()
                      ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-400 dark:border-rose-600 focus:ring-rose-400 animate-pulse'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 focus:ring-yellow-400'
                  }`}
                  placeholder={convertPreview.plate_missing ? 'Form\'da plaka yok — buraya yazın' : '1-ABC-123'}
                  data-testid="convert-edit-license-plate"
                  autoFocus={convertPreview.plate_missing}
                />

                <PreviewRow label="Sürücü" value={convertPreview.route_preview?.driver_name || '—'} highlight />
                <PreviewRow label="Başlangıç KM" value={convertPreview.route_preview?.km_start ?? '—'} mono />
                <PreviewRow label="Bitiş KM" value={convertPreview.route_preview?.km_end ?? '—'} mono />
                <PreviewRow label="Toplam KM" value={`${convertPreview.route_preview?.km_total ?? 0} km`} mono highlight />
                <PreviewRow label="Başlangıç Saat" value={convertPreview.route_preview?.start_time || '—'} mono />
                <PreviewRow label="Bitiş Saat" value={convertPreview.route_preview?.end_time || '—'} mono />
                <PreviewRow label="Mola" value={convertPreview.route_preview?.break_time || '—'} mono />
                <PreviewRow label="Çalışma Saati" value={`${convertPreview.route_preview?.work_hours ?? 0} sa`} mono />

                {/* Editable: Ücret/saat */}
                <div className="text-slate-500 dark:text-slate-400 self-center">Ücret/saat</div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">€</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={previewEdits.hourly_rate}
                    onChange={(e) => setPreviewEdits((s) => ({ ...s, hourly_rate: e.target.value }))}
                    className="font-mono bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded px-2 py-0.5 text-xs w-20 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                    data-testid="convert-edit-hourly-rate"
                  />
                </div>

                {/* Editable: Başlangıç Maliyeti */}
                <div className="text-slate-500 dark:text-slate-400 self-center">Başlangıç Maliyet</div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">€</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={previewEdits.start_cost}
                    onChange={(e) => setPreviewEdits((s) => ({ ...s, start_cost: e.target.value }))}
                    className="font-mono bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded px-2 py-0.5 text-xs w-20 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                    data-testid="convert-edit-start-cost"
                  />
                </div>

                <PreviewRow label="Toplam Maliyet" value={`€${(editedTotals?.totalCost ?? 0).toFixed(2)}`} mono highlight />
              </div>

              {(convertPreview.route_preview?.notes) && (
                <div className="text-[10px] text-slate-500 italic">Notlar: {convertPreview.route_preview.notes}</div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={() => setConvertPreview(null)} data-testid="convert-cancel-btn">
                İptal
              </Button>
              <Button
                onClick={handleConfirmConvert}
                disabled={(converting === convertPreview.assignment_id) || (convertPreview.plate_missing && !previewEdits.license_plate?.trim())}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="convert-confirm-btn"
              >
                {converting === convertPreview.assignment_id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Truck className="h-4 w-4" />}
                {convertPreview.already_converted ? 'Mevcut Routela Eşle' : 'Onayla ve Aktar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Önizleme satırı — kısa key+value formatlaması için minik yardımcı
function PreviewRow({ label, value, mono = false, highlight = false }) {
  return (
    <>
      <div className="text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`${mono ? 'font-mono' : ''} ${highlight ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-200'}`}>
        {value === '' || value === null || value === undefined ? '—' : value}
      </div>
    </>
  );
}
